import { fetchBanners, fetchPrices, fetchSupplyToEarnAPY } from "@/apis/lendingRequests"
import { NextAPIRequests } from "@/apis/nextAPIRequests"
import { bannersAtom } from "@/atoms/bannersState"
import { poolsAtom } from "@/atoms/pools"
import { renecPriceDataAtom } from "@/atoms/priceState"
import { connectionAtom, networkAtom } from "@/atoms/settings"
import { solendUSDSObligationsStatsAtom } from "@/atoms/solendObligations"
import { selectedSolendPoolAtom } from "@/atoms/solendPools"
import { USDS_STE, supplyToEarnAPYAtom } from "@/atoms/supplyToEarnState"
import { Seo } from "@/components/base"
import { Dashboard } from "@/components/dashboard"
import Layout from "@/components/layout"
import useMarketSelection from "@/hooks/useMarketSelection"
import useSetupOnChain from "@/hooks/useSetupOnChain"
import useTrans from "@/hooks/useTrans"
import useWalletConnect from "@/hooks/useWalletConnect"
import { IS_MAINNET, LOCALE_EN, LOCALE_VI, isRunningInCypress } from "@/utils/constants"
import { errorWrapper } from "@/utils/helpers"
import { fetchSolendUSDSObligationStats } from "@/utils/solend/solend.utils"
import { sendEventLog } from "@/utils/tracking-events"
import { useRollbar } from "@rollbar/react"
import { useAtomValue, useSetAtom } from "jotai"
import { GetStaticProps } from "next"
import { useRouter } from "next/router"
import { useEffect } from "react"
import { QueryClient, dehydrate, useQuery } from "react-query"

export default function DashboardPage() {
  const trans = useTrans()
  const router = useRouter()
  const locale = router.locale === LOCALE_VI ? LOCALE_VI : LOCALE_EN
  // for switching network
  const network = useAtomValue(networkAtom)
  const { isSolanaChain } = useWalletConnect()

  const { data: poolValues } = useQuery(
    ["fetchPools", network],
    () => {
      return NextAPIRequests.fetchPools({
        network: network,
      })
    },
    {
      staleTime: 10000,
      refetchOnWindowFocus: false,
    },
  )

  const { data: bannerData, refetch: refetchListBanners } = useQuery(["fetchBanners"], () => fetchBanners(locale), {
    staleTime: 10000,
  })
  const { data: pricesData } = useQuery(["fetchPrices"], () => fetchPrices(), {
    staleTime: 10000,
  })
  const { data: supplyToEarnAPY } = useQuery(
    ["fetchSupplyToEarnAPY", network],
    () => {
      return fetchSupplyToEarnAPY(network)
    },
    {
      staleTime: 10000,
    },
  )
  const connection = useAtomValue(connectionAtom)
  const selectedSolendPool = useAtomValue(selectedSolendPoolAtom)
  const { data: solendUSDSObligationsStats } = useQuery(
    ["fetchSolendUSDSObligationsStats", selectedSolendPool?.reserves, network, isSolanaChain],
    () => {
      if (IS_MAINNET && isSolanaChain && selectedSolendPool?.reserves.length > 0) {
        return fetchSolendUSDSObligationStats(network, connection, selectedSolendPool)
      }
      return null
    },
    {
      staleTime: 10000,
    },
  )

  const rollbar = useRollbar()
  const setPools = useSetAtom(poolsAtom)
  const setBanners = useSetAtom(bannersAtom)
  const setSupplyToEarnAPY = useSetAtom(supplyToEarnAPYAtom)
  const setRenecPrices = useSetAtom(renecPriceDataAtom)
  const setSolendUSDSObligationsStats = useSetAtom(solendUSDSObligationsStatsAtom)

  useEffect(() => {
    refetchListBanners()
  }, [refetchListBanners, locale])

  useEffect(() => {
    if (poolValues && !poolValues.error && poolValues.length > 0) {
      const pools = JSON.parse(poolValues)
      setPools(pools)
    }
  }, [poolValues, setPools])

  useSetupOnChain()

  useEffect(() => {
    if (bannerData?.error) {
      setBanners([])
    } else {
      setBanners(bannerData)
    }
  }, [bannerData, rollbar, setBanners])

  useEffect(() => {
    const newSupplyToEarnAPY = []
    if (IS_MAINNET && isSolanaChain) {
      newSupplyToEarnAPY.push(USDS_STE)
    }
    if (supplyToEarnAPY && !supplyToEarnAPY.error) {
      newSupplyToEarnAPY.push(...supplyToEarnAPY)
    }
    setSupplyToEarnAPY(newSupplyToEarnAPY)
  }, [isSolanaChain, setSupplyToEarnAPY, supplyToEarnAPY])

  useEffect(() => {
    if (solendUSDSObligationsStats) {
      setSolendUSDSObligationsStats(solendUSDSObligationsStats)
    }
  }, [isSolanaChain, setSolendUSDSObligationsStats, solendUSDSObligationsStats])

  useEffect(() => {
    if (pricesData && !pricesData.error && !isRunningInCypress) {
      setRenecPrices(pricesData)
    }
  }, [setRenecPrices, pricesData])

  useEffect(() => {
    sendEventLog("view_relend_dashboard", {
      isMainnet: IS_MAINNET,
    })
  }, [])

  useEffect(() => {
    if (!isRunningInCypress) return

    const fetchData = async () => {
      const supplyToEarnAPY = await fetchSupplyToEarnAPY(network)
      if (supplyToEarnAPY && !supplyToEarnAPY.error) {
        setSupplyToEarnAPY(supplyToEarnAPY)
      }

      const pricesData = await fetchPrices()
      if (pricesData && !pricesData.error) {
        setRenecPrices(pricesData)
      }
    }

    fetchData()
  }, [network, setRenecPrices, setSupplyToEarnAPY])

  useMarketSelection()

  return (
    <>
      <Seo
        data={{
          title: `${trans.seo.seo_title_dashboard}`,
          description: trans.seo.seo_description_dashboard,
        }}
      />
      <Layout>
        <Dashboard />
      </Layout>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const queryClient = new QueryClient()

  await Promise.all([
    errorWrapper(() =>
      queryClient.prefetchQuery(["fetchBanners"], () => fetchBanners(locale ?? "en"), { staleTime: 10000 }),
    ),
    // errorWrapper(() =>
    //   queryClient.prefetchQuery(["fetchSupplyToEarnAPY"], () => fetchSupplyToEarnAPY(), { staleTime: 10000 }),
    // ),
    errorWrapper(() => queryClient.prefetchQuery(["fetchPrices"], () => fetchPrices(), { staleTime: 10000 })),
  ])

  return {
    props: {
      locale,
      dehydratedState: dehydrate(queryClient),
    },
  }
}
